import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMetadata } from "../context/MetadataContext";
import { useEditUI } from "../context/EditUIPreferencesContext";
import { createAutosave } from "../utils/autosave";
import {
  fetchWebsiteDefaultsIndex,
  fetchWebsiteDefaultEntry,
  patchWebsiteDefaultEntry,
  switchWebsiteDefaultsPreset,
  revertWebsiteDefaultsPreset,
  fetchDefaultsInternalElement,
  patchDefaultsInternalElement,
} from "./defaultsApi";

const DefaultsEditContext = createContext(null);

export const useDefaultsEdit = () => {
  const ctx = useContext(DefaultsEditContext);
  if (!ctx) throw new Error("useDefaultsEdit must be used within DefaultsEditProvider");
  return ctx;
};

const PRESET_NAMES = ["midnight", "eclipse", "twilight", "autumn", "autumn_dark", "sky", "night_sky", "notebook", "journal", "qadim", "azraq", "dhahab", "nuhas", "clean", "crisp", "dark_red", "onyx", "ink", "editorial", "gazette", "adventure", "expedition", "forest", "arctic", "ocean", "minimal", "golden", "silver", "bronze", "bloom", "sepia", "lavender", "mint", "dark", "verdant", "elegant", "sienna", "diffused", "diffused_light", "sand", "sand_light", "voyage", "journey", "ember", "nostalgia", "vesper", "heirloom", "patina", "cosmo", "library", "atlas", "codex", "fifty", "sixty", "seventy", "surf", "coastal", "hibiscus", "umbra", "alabaster", "tech", "window", "glass", "blurred", "aurora", "dusk", "constellations", "lyra", "polaris", "lab", "atom", "fourier", "martingale", "videogame", "arcade", "wire", "wire_trace", "wire_heat", "wire_ink", "flierflies", "foxfire", "lava_lamp", "magma", "plasma", "shades", "blush", "moss", "desert", "oasis", "mesa", "dune"];
const FORBIDDEN_TYPES = new Set(["image", "file", "image_collection"]);

const typeToMetadataKey = {
  // Style tokens
  background: "background",
  base_style: "baseStyle",
  character_style: "characterStyle",
  container_style: "containerStyle",
  // Core editor types
  component: "component",
  hero: "hero",
  title: "title",
  text: "text",
  video: "video",
  button: "button",
  button_collection: "buttonCollection",
  // Navigation
  navbar: "navbar",
  navbar_section: "navbarSection",
  sidebar: "sidebar",
  sidebar_section: "sidebarSection",
  footer: "footer",
};

function normalizeType(type) {
  if (type == null) return null;
  const t = String(type).trim();
  return t ? t.toLowerCase() : null;
}

function mergeAndNormalizeType(obj, typeHint) {
  const hinted = normalizeType(typeHint);
  const existing = normalizeType(obj?.type);
  const type = existing || hinted || null;

  if (!obj || typeof obj !== "object") return type ? { type } : {};
  // Ensure type is always normalized, even if backend returns "Hero" or "Component"
  return type ? { ...obj, type } : obj;
}

export const DefaultsEditProvider = ({ children, websiteId, isOpen }) => {
  const metadata = useMetadata();
  const { groupViewMode } = useEditUI();

  const autosaveRef = useRef(null);
  const hintTimeoutRef = useRef(null);
  const loadSeqRef = useRef(0);
  useEffect(() => {
    if (!autosaveRef.current) autosaveRef.current = createAutosave(700);
  }, []);

  const [indexLoading, setIndexLoading] = useState(false);
  const [entryLoading, setEntryLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPresetName, setCurrentPresetName] = useState(null);
  const [canRevert, setCanRevert] = useState(false);
  const [revertToPresetName, setRevertToPresetName] = useState(null);
  const [entries, setEntries] = useState([]);
  const [hint, setHint] = useState(null);

  const [selectedDefaultKey, setSelectedDefaultKey] = useState(null);
  const [selectedDefaultType, setSelectedDefaultType] = useState(null);
  const selectedDefaultKeyRef = useRef(null);
  useEffect(() => {
    selectedDefaultKeyRef.current = selectedDefaultKey;
  }, [selectedDefaultKey]);

  const [mode, setMode] = useState("default"); // 'default' | 'internal'
  const [selectedInternal, setSelectedInternal] = useState(null); // { type, id, label, parentDefaultKey }

  const [selectedObject, setSelectedObject] = useState(null);
  const [parentPreviewObject, setParentPreviewObject] = useState(null);
  const [saveStatus, setSaveStatus] = useState("saved"); // saved | saving | error

  const refreshIndex = useCallback(async () => {
    if (!websiteId) return;
    setIndexLoading(true);
    setError(null);
    try {
      const data = await fetchWebsiteDefaultsIndex(websiteId);
      const nextEntries = Array.isArray(data.entries) ? data.entries : [];
      setEntries(nextEntries);
      setCurrentPresetName(data.current_preset_name ?? null);
      setCanRevert(Boolean(data.can_revert));
      setRevertToPresetName(data.revert_to_preset_name ?? null);
      return data;
    } catch (e) {
      setError(e?.message || "Failed to load defaults.");
      return null;
    } finally {
      setIndexLoading(false);
    }
  }, [websiteId]);

  const selectDefaultKey = useCallback((key) => {
    setMode("default");
    setSelectedInternal(null);
    setSelectedDefaultKey(key);
  }, []);

  const openInternalElement = useCallback((element) => {
    if (!element || !element.type || !element.id) return;
    if (FORBIDDEN_TYPES.has(element.type)) return;
    setMode("internal");
    setSelectedInternal({
      type: element.type,
      id: element.id,
      label: element.label,
      parentDefaultKey: selectedDefaultKey,
    });
  }, [selectedDefaultKey]);

  const closeInternalElement = useCallback(() => {
    setMode("default");
    setSelectedInternal(null);
  }, []);

  // Ensure defaults index loads when modal opens
  useEffect(() => {
    if (!isOpen || !websiteId) return;
    (async () => {
      const data = await refreshIndex();
      if (!data) return;
      // Pick an initial selection if missing
      if (!selectedDefaultKeyRef.current) {
        const first = (Array.isArray(data.entries) ? data.entries : [])[0];
        if (first?.key) setSelectedDefaultKey(first.key);
      }
    })();
  }, [isOpen, websiteId, refreshIndex]);

  // Keep selected type in sync with selected key
  useEffect(() => {
    if (!selectedDefaultKey) {
      setSelectedDefaultType(null);
      return;
    }
    const entry = entries.find((e) => e.key === selectedDefaultKey);
    setSelectedDefaultType(entry?.type || null);
  }, [entries, selectedDefaultKey]);

  // Load selected object (default key or internal element)
  useEffect(() => {
    if (!isOpen || !websiteId) return;

    loadSeqRef.current += 1;
    const seq = loadSeqRef.current;

    const load = async () => {
      setEntryLoading(true);
      setError(null);
      try {
        if (mode === "internal" && selectedInternal?.type && selectedInternal?.id) {
          const data = await fetchDefaultsInternalElement(websiteId, selectedInternal.type, selectedInternal.id);
          if (seq !== loadSeqRef.current) return;
          setSelectedObject(mergeAndNormalizeType(data, selectedInternal.type));
          return;
        }
        if (selectedDefaultKey) {
          const entryType =
            entries.find((e) => e.key === selectedDefaultKey)?.type || selectedDefaultType || null;
          const data = await fetchWebsiteDefaultEntry(websiteId, selectedDefaultKey);
          if (seq !== loadSeqRef.current) return;
          setSelectedObject(entryType ? mergeAndNormalizeType(data, entryType) : mergeAndNormalizeType(data, null));
        } else {
          if (seq !== loadSeqRef.current) return;
          setSelectedObject(null);
        }
      } catch (e) {
        if (seq !== loadSeqRef.current) return;
        setError(e?.message || "Failed to load default entry.");
        setSelectedObject(null);
      } finally {
        if (seq !== loadSeqRef.current) return;
        setEntryLoading(false);
      }
    };

    load();
  }, [isOpen, websiteId, mode, selectedInternal, selectedDefaultKey, entries, selectedDefaultType]);

  // When editing an internal element, keep a stable preview of the parent default entry.
  useEffect(() => {
    if (!isOpen || !websiteId) return;
    if (mode !== "internal" || !selectedInternal?.parentDefaultKey) {
      setParentPreviewObject(null);
      return;
    }

    const loadParent = async () => {
      try {
        const parentKey = selectedInternal.parentDefaultKey;
        const entryType = entries.find((e) => e.key === parentKey)?.type || null;
        const data = await fetchWebsiteDefaultEntry(websiteId, parentKey);
        setParentPreviewObject(entryType ? mergeAndNormalizeType(data, entryType) : mergeAndNormalizeType(data, null));
      } catch (e) {
        // Preview is best-effort; don't block editing
        setParentPreviewObject(null);
      }
    };

    loadParent();
  }, [isOpen, websiteId, mode, selectedInternal, entries]);

  const previewObject = useMemo(() => {
    if (mode === "internal" && parentPreviewObject) return parentPreviewObject;
    return selectedObject;
  }, [mode, parentPreviewObject, selectedObject]);

  const editMetadata = useMemo(() => {
    const type = normalizeType(selectedObject?.type);
    if (!type) return null;
    const key = typeToMetadataKey[type] || type;
    return metadata?.[key] || null;
  }, [metadata, selectedObject]);

  const getPatchTarget = useCallback(() => {
    if (!websiteId) return null;
    if (mode === "internal" && selectedInternal?.type && selectedInternal?.id) {
      return { kind: "internal", ...selectedInternal };
    }
    if (selectedDefaultKey) {
      return { kind: "default", defaultKey: selectedDefaultKey };
    }
    return null;
  }, [mode, selectedInternal, selectedDefaultKey, websiteId]);

  const updateField = useCallback((field, value) => {
    if (!selectedObject) return;
    const target = getPatchTarget();
    if (!target) return;

    setSaveStatus("saving");
    setError(null);

    // optimistic local update
    setSelectedObject((prev) => (prev ? { ...prev, [field]: value } : prev));

    const payload = { [field]: value };
    if (selectedObject.version !== undefined && selectedObject.version !== null) {
      payload.version = selectedObject.version;
    }

    const onSuccess = (data) => {
      setSelectedObject((prev) => {
        const merged = { ...(prev || {}), ...(data || {}) };
        // Keep type stable (backend style serializers may omit it)
        return prev?.type ? { ...merged, type: prev.type } : merged;
      });
      setSaveStatus("saved");
    };
    const onError = (err) => {
      const msg =
        err?.response?.error ||
        err?.message ||
        (typeof err === "string" ? err : "Failed to save.");
      setError(msg);
      setSaveStatus("error");
    };

    try {
      if (target.kind === "default") {
        autosaveRef.current.schedule(
          `/api/websites/${websiteId}/defaults/${target.defaultKey}/`,
          payload,
          onSuccess,
          onError,
          "PATCH",
        );
      } else {
        autosaveRef.current.schedule(
          `/api/websites/${websiteId}/defaults/internal/${target.type}/${target.id}/`,
          payload,
          onSuccess,
          onError,
          "PATCH",
        );
      }
    } catch (e) {
      onError(e);
    }
  }, [getPatchTarget, selectedObject, websiteId]);

  const updateMultipleFields = useCallback((updates) => {
    if (!updates || typeof updates !== "object") return;
    Object.entries(updates).forEach(([field, value]) => updateField(field, value));
  }, [updateField]);

  const switchPreset = useCallback(async (presetName) => {
    if (!websiteId) return;
    setError(null);
    setIndexLoading(true);
    try {
      // Switching presets changes ids across the defaults graph; reset internal selection.
      setMode("default");
      setSelectedInternal(null);
      const res = await switchWebsiteDefaultsPreset(websiteId, presetName);
      if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);
      setHint("Preset switched.");
      hintTimeoutRef.current = setTimeout(() => setHint(null), 3000);
      const data = await refreshIndex();
      // reload selected entry (effect will trigger via current state)
      if (data?.current_preset_name) setCurrentPresetName(data.current_preset_name);
    } catch (e) {
      setError(e?.message || "Failed to switch preset.");
    } finally {
      setIndexLoading(false);
    }
  }, [refreshIndex, websiteId]);

  const revertPreset = useCallback(async () => {
    if (!websiteId) return;
    setError(null);
    setIndexLoading(true);
    try {
      // Revert changes ids across the defaults graph; reset internal selection.
      setMode("default");
      setSelectedInternal(null);
      const res = await revertWebsiteDefaultsPreset(websiteId);
      if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);
      if (res?.current_preset_name) {
        setHint("Preset reverted.");
        hintTimeoutRef.current = setTimeout(() => setHint(null), 3000);
      }
      const data = await refreshIndex();
      if (data?.current_preset_name) setCurrentPresetName(data.current_preset_name);
    } catch (e) {
      setError(e?.message || "Failed to revert preset.");
    } finally {
      setIndexLoading(false);
    }
  }, [refreshIndex, websiteId]);

  const value = useMemo(() => {
    return {
      websiteId,
      groupViewMode,
      presets: PRESET_NAMES,
      // index state
      entries,
      indexLoading,
      currentPresetName,
      canRevert,
      revertToPresetName,
      refreshIndex,
      hint,
      // selection state
      selectedDefaultKey,
      selectedDefaultType,
      selectDefaultKey,
      mode,
      selectedInternal,
      openInternalElement,
      closeInternalElement,
      // object state
      selectedObject,
      previewObject,
      entryLoading,
      editMetadata,
      // save state
      saveStatus,
      error,
      // actions
      updateField,
      updateMultipleFields,
      switchPreset,
      revertPreset,
    };
  }, [
    websiteId,
    groupViewMode,
    entries,
    indexLoading,
    currentPresetName,
    canRevert,
    revertToPresetName,
    refreshIndex,
    hint,
    selectedDefaultKey,
    selectedDefaultType,
    selectDefaultKey,
    mode,
    selectedInternal,
    openInternalElement,
    closeInternalElement,
    selectedObject,
    previewObject,
    entryLoading,
    editMetadata,
    saveStatus,
    error,
    updateField,
    updateMultipleFields,
    switchPreset,
    revertPreset,
  ]);

  return <DefaultsEditContext.Provider value={value}>{children}</DefaultsEditContext.Provider>;
};

